import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as Connect } from "assets/svg/connect.svg";
import { ReactComponent as Disconnect } from "assets/svg/disconnect.svg";
import { Button } from "react-bootstrap";

import GoogleCalendar from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/google-calendar/GoogleCalendar";
import { useSubscription } from "contexts/subscription-context";
import { useUser } from "contexts/UserContext";

import getSelectedGCals from "services/gcal/getSelectedGCals";
import { AmplitudePageName, track } from "utils/amplitude";
import SelectGCalsModal from "../../views/one-one-sessions/components/layout/components/google-calendar/SelectGCalsModal";
import styles from "./Integration.module.scss";
import messages from "messages";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-integration";

function getGCalAccountName(user: ReturnType<typeof useUser>["user"]) {
  if (!user) {
    return "";
  }
  const strings = [user.first_name, user.last_name].filter((el) => el);
  if (strings.length === 0) {
    return `(${messages.noTitle})`;
  }
  return strings.join(" ");
}

export const GCalConnectButton = () => {
  const { user } = useUser();
  const [connected, setConnected] = useState(
    user?.has_calendar_google_connected,
  );
  const { canAccessFeature } = useSubscription();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showSelectCalendarsModal, setShowSelectCalendarsModal] =
    useState(false);
  const [GCals, setGCals] = useState<CalendarEntry[]>([]);

  useEffect(() => {
    if (!connected) return;

    getSelectedGCals()
      .then((res) => {
        if (res.length === 0) {
          setShowSelectCalendarsModal(true);
          return;
        }
        setGCals(res);
      })
      .catch(console.error);
  }, [connected]);

  const connectedAccounts = useMemo(
    () => (!connected ? [] : [getGCalAccountName(user)]),
    [connected, user],
  );

  return (
    <>
      {!connected ? (
        <Button
          disabled={!canAccessFeature("calendar-sync")}
          className={styles["integration__gcal-connect-button"]}
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "connect-button-google-cal", "click");
            setShowConnectModal(true);
          }}
        >
          <Connect />
          Connect
        </Button>
      ) : (
        <Button
          variant={"outline-primary"}
          className={styles["integration__gcal-disconnect-button"]}
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "disconnect-button-google-cal", "click");
            setShowConnectModal(true);
          }}
        >
          <Disconnect />
          Disconnect
        </Button>
      )}
      {showSelectCalendarsModal && (
        <SelectGCalsModal
          showSelectCalendarsModal={showSelectCalendarsModal}
          setShowSelectCalendarsModal={setShowSelectCalendarsModal}
          analyticsPageName={AMPLITUDE_PAGE_NAME}
          initialSyncedCalendars={GCals}
          setGCals={setGCals}
          setVisibleCalendarIds={() => {}}
        />
      )}
      {showConnectModal && (
        <GoogleCalendar
          connected={connected}
          setConnected={setConnected}
          showConnectModal={showConnectModal}
          setShowConnectModal={setShowConnectModal}
          connectedAccounts={connectedAccounts}
          analyticsPageName={AMPLITUDE_PAGE_NAME}
          setGCals={setGCals}
          setShowSelectCalendarsModal={setShowSelectCalendarsModal}
        />
      )}
    </>
  );
};
